import { CdkTrapFocus } from '@angular/cdk/a11y';
import { Directive, ElementRef, effect, inject } from '@angular/core';
import { Combobox, ComboboxPopup } from '@angular/aria/combobox';
import { ComboboxDialogPattern } from '@angular/aria/private';

/**
 * Popup combobox inline (non `<dialog showModal>`).
 * Mantiene il pattern dialog di Angular Aria senza il top layer fisso del browser.
 * Con `cdkTrapFocus`: Tab resta nel popup finché non si chiude (Escape o selezione).
 */
@Directive({
  selector: '[ariaComboboxDialogPanel]',
  hostDirectives: [ComboboxPopup, CdkTrapFocus],
  host: {
    '[attr.aria-modal]': 'combobox.expanded() ? "true" : null',
    '[attr.data-open]': 'combobox.expanded()',
    '[attr.role]': 'combobox.expanded() ? "dialog" : null',
    '(keydown)': '_pattern.onKeydown($event)',
    '(click)': '_pattern.onClick($event)',
  },
})
export class AriaComboboxDialogPanelDirective {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly focusTrap = inject(CdkTrapFocus);
  protected readonly combobox = inject(Combobox);
  private readonly popup = inject(ComboboxPopup, { optional: true });

  protected readonly _pattern = new ComboboxDialogPattern({
    id: () => '',
    element: () => this.elementRef.nativeElement,
    combobox: this.combobox._pattern,
  });

  constructor() {
    this.popup?._controls.set(this._pattern);
    this.focusTrap.autoCapture = false;

    effect(() => {
      const expanded = this.combobox.expanded();
      const el = this.elementRef.nativeElement;

      this.focusTrap.enabled = expanded;

      if (expanded) {
        el.removeAttribute('inert');
        el.removeAttribute('aria-hidden');
      } else {
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true');
      }
    });
  }
}
